import React from 'react';
import { BaseButton } from './styled/BaseButton';
import { LotteryDetails } from '../contracts/interfaces';
import styled, { css } from 'styled-components';
import { Sparkles, ShoppingCart, Wallet } from 'lucide-react';
import { formatEth } from '../utils/format';

/* Design tokens matching the number selector */
const tokens = {
  bgCard: '#1e1e2e',
  bgInset: '#0f172a',
  textMuted: '#94a3b8',
  borderSubtle: '#334155',
  successBg: 'rgba(34, 197, 94, 0.15)',
  successText: '#4ade80',
};

const ActionPanelContainer = styled.div`
  background: ${tokens.bgCard};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const PriceDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
  background: ${tokens.bgInset};
  border: 1px solid ${tokens.borderSubtle};
  border-radius: 12px;
`;

const PriceLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PriceValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ActionButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

interface LotteryActionPanelProps {
  lotteryData: LotteryDetails | null;
  selectedNumbers: number[];
  pendingWithdrawal: bigint;
  isLoading: boolean;
  purchaseTicket: (numbers: number[]) => Promise<void>;
  withdrawWinnings: () => Promise<void>;
  setSelectedNumbers: (numbers: number[]) => void;
  timeRemaining: number;
}

export const LotteryActionPanel: React.FC<LotteryActionPanelProps> = ({
  lotteryData,
  selectedNumbers,
  pendingWithdrawal,
  isLoading,
  purchaseTicket,
  withdrawWinnings,
  setSelectedNumbers,
  timeRemaining,
}) => {
  const maxNumbers = lotteryData?.numbers || 7;
  const canPurchaseTicket =
    lotteryData && selectedNumbers.length === lotteryData.numbers;
  const hasPendingWithdrawal = pendingWithdrawal > BigInt(0);
  const isLotteryActive =
    lotteryData && lotteryData.state === 1 && timeRemaining > 15;

  const handleQuickPick = () => {
    if (!lotteryData || !isLotteryActive) return;

    const totalNumbers = lotteryData.numbers;
    const maxNumber = lotteryData.maxNum;

    const randomNumbers: number[] = [];
    for (let i = 0; i < totalNumbers; i++) {
      let randomNumber: number;
      do {
        randomNumber = Math.floor(Math.random() * maxNumber) + 1;
      } while (randomNumbers.includes(randomNumber));
      randomNumbers.push(randomNumber);
    }

    const sortedNumbers = randomNumbers.sort((a, b) => a - b);
    setSelectedNumbers(sortedNumbers);
  };

  const isQuickPickDisabled =
    isLoading || !lotteryData || !isLotteryActive || timeRemaining <= 8;

  return (
    <ActionPanelContainer>
      {/* Price Display - shows when selection is complete */}
      {lotteryData && (
        <PriceDisplay>
          <PriceLabel>Ticket Price</PriceLabel>
          <PriceValue>{lotteryData.formattedTicketPrice || formatEth(lotteryData.price, 4)}</PriceValue>
        </PriceDisplay>
      )}

      {/* Action Buttons */}
      <ActionButtonContainer>
        <BaseButton
          variant="green"
          onClick={withdrawWinnings}
          disabled={!hasPendingWithdrawal || isLoading}
          fullWidth
          icon={<Wallet size={18} />}
        >
          {isLoading
            ? 'Processing...'
            : hasPendingWithdrawal 
              ? `Withdraw ${formatEth(pendingWithdrawal, 4)}`
              : 'No Winnings'}
        </BaseButton>

        <BaseButton
          variant="yellow"
          onClick={handleQuickPick}
          disabled={isQuickPickDisabled}
          fullWidth
          icon={<Sparkles size={18} />}
        >
          {isQuickPickDisabled && (!isLotteryActive || timeRemaining <= 8)
            ? 'Unavailable'
            : 'Quick Pick'}
        </BaseButton>

        <BaseButton
          variant="primary"
          onClick={() => purchaseTicket(selectedNumbers)}
          disabled={
            !canPurchaseTicket ||
            isLoading ||
            !isLotteryActive ||
            timeRemaining <= 8
          }
          fullWidth
          icon={<ShoppingCart size={18} />}
        >
          {isLoading
            ? 'Processing...'
            : !isLotteryActive || timeRemaining <= 8
            ? 'Not Active'
            : canPurchaseTicket 
              ? 'Buy Ticket'
              : `Pick ${maxNumbers - selectedNumbers.length} more`}
        </BaseButton>
      </ActionButtonContainer>
    </ActionPanelContainer>
  );
};
